<template>
    <div class="m-4 mt-2">
        <div class="mb-6">
            {{ config.subtitle }}
        </div>
        <DForm :errors="errors">
            <DField id="value">
                <DInput
                    v-model="search"
                    :placeholder="config.search_placeholder || 'Search...'"
                    left-icon="ph:magnifying-glass"
                />
            </DField>
        </DForm>
        <DAlert
            v-if="config.alert"
            class="mt-6"
        >
            <template #title>
                {{ config.alert.title }}
            </template>
            {{ config.alert.body }}
            <DButton
                v-if="config.alert.button_url"
                class="mt-4"
                theme="text-link"
                link-color="alert"
                @click="onAction(config.alert.button_url)"
            >
                {{ config.alert.button_text }}
            </DButton>
        </DAlert>

        <div
            v-if="searching"
            class="w-full h-50 my-4 flex items-center justify-center"
        >
            <UILoading class="w-12 h-12" />
        </div>
        <div
            v-else-if="!results.length && search.length && !hasErrors"
            class="my-4"
        >
            <Empty icon="empty-search">
                <template #title>
                    No result found
                </template>

                <template #subtitle>
                    Please try a different search term
                </template>
            </Empty>
        </div>
        <div
            v-else
            class="w-full"
        >
            <div
                v-for="(result, index) in results"
                :key="`result-${index}`"
                class="w-full flex justify-between items-center"
                @click="onItemClick(result)"
            >
                <div>
                    <div class="content-ux-regular mt-3 mb-1.5">
                        {{ result.title }}
                    </div>
                    <div class="content-sm-ux-regular mb-1.5">
                        {{ result.description }}
                    </div>
                    <div class="content-xs-ux-regular">
                        {{ result.subtext }}
                    </div>
                </div>
                <DIcon
                    v-if="selected === result.value"
                    icon="heroicons:check-20-solid"
                    size="w-6 h-6"
                />
            </div>
        </div>
        <Teleport :to="footerMenu?.$el">
            <div class="pb-(--beyond-safe-area-inset-bottom)">
                <div class="p-4 border-t border-neutral-base flex surface-primary w-full mt-auto gap-2">
                    <DButton
                        v-if="config.action_url"
                        wide
                        :disabled="!selected"
                        @click="onAction(config.action_url)"
                    >
                        {{ config.action_text || 'Continue' }}
                    </DButton>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script setup>
    import { useRouter } from 'vue-router'
    import {
        DAlert, DButton, DField, DForm, DIcon, DInput,
    } from '@digistorm/spark'
    import { isCancel } from 'axios'
    import { useLoadingOverlays } from '@/composables/loadingOverlays'
    import linkType from '@/constants/linkType'
    import Empty from '@/components/ui/Empty.vue'

    const router = useRouter()
    const moduleStore = useModuleStore()
    const moduleHelpers = useModuleHelpers()
    const loadingOverlays = useLoadingOverlays()

    const props = defineProps({
        config: Object,
    })

    const footerMenu = inject('footerMenu')

    const searching = ref(false)
    const search = ref('')
    const results = ref([])
    const errors = ref({})
    const selected = ref(null)

    const hasErrors = computed(() => {
        return Object.keys(errors.value).length > 0
    })

    const onAction = (url) => {
        loadingOverlays.push()
        const button = {
            link: {
                type: linkType.apiAction,
                url,
            },
        }
        if (selected.value) {
            button.link.payload = { value: selected.value }
        }
        return moduleHelpers.handleClick(router, button, null, false)
            .finally(() => {
                loadingOverlays.pop()
            })
    }

    const onItemClick = (item) => {
        if (selected.value === item.value) {
            selected.value = null
            return
        }
        selected.value = item.value
    }

    const searchDebounced = debounce(() => {
        results.value = []
        if (!search.value || !search.value.length) {
            searching.value = false
            return
        }
        searching.value = true
        errors.value = {}
        moduleStore.loadContent(
            props.config.search_url,
            { value: search.value },
        )
            .then(({ data }) => {
                searching.value = false
                results.value = data.items
            })
            .catch((e) => {
                if (isCancel(e)) {
                    return e
                }
                searching.value = false
                if (e.response?.status === 422 && e.response?.data?.errors) {
                    errors.value = e.response.data.errors
                }
                if (moduleHelpers.isActionException(e)) {
                    moduleHelpers.handleActionException(router, e.response.data)
                }
            })
    }, 300)

    watch(search, () => {
        searchDebounced()
    })

</script>
